// src/hooks/useQuoteVocabularies.js
// SEAM 6 VERIFIER: qfup-7d3e9a41-hook
//
// Live follow-up vocabularies for an org, from
// organizations/{orgId}/settings/quoteVocabularies (staff-read, server-write).
// Falls back to the frozen defaults while the document doesn't exist yet, which
// is the normal state for every org until someone edits the lists.
// Operator surfaces only — portal customers cannot read this document.
//
// `revision` is returned alongside the lists because the settings editor must
// send back the revision its EDITS STARTED FROM, not whatever the database
// holds at save time. That is what makes the optimistic lock in
// saveQuoteVocabularies able to fire at all (the Seam 4 bug-4 lesson).
//
// resolveQuoteVocabularies never throws, so a malformed document degrades to
// the defaults rather than breaking the Log Follow-up modal.
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
    resolveQuoteVocabularies,
    FOLLOW_UP_METHOD_DEFAULTS,
    FOLLOW_UP_RESULT_DEFAULTS,
    CLOSE_REASON_DEFAULTS,
    PRICE_FEEDBACK_DEFAULTS,
} from '../utils/quoteSchema';

// qclose-3e8a5c17 — the close lists ride the same document (Seam 9).
const FALLBACK = {
    followUpMethods: FOLLOW_UP_METHOD_DEFAULTS,
    followUpResults: FOLLOW_UP_RESULT_DEFAULTS,
    closeReasons: CLOSE_REASON_DEFAULTS,
    priceFeedbackOptions: PRICE_FEEDBACK_DEFAULTS,
    revision: 0,
    loaded: false,
};

export default function useQuoteVocabularies(orgId) {
    const [vocab, setVocab] = useState(FALLBACK);

    useEffect(() => {
        if (!orgId) { setVocab(FALLBACK); return undefined; }
        return onSnapshot(
            doc(db, `organizations/${orgId}/settings`, 'quoteVocabularies'),
            (snap) => {
                const data = snap.exists() ? snap.data() : null;
                const lists = resolveQuoteVocabularies(data);
                setVocab({
                    ...lists,
                    revision: (data && Number.isInteger(data.revision)) ? data.revision : 0,
                    loaded: true,
                });
            },
            (err) => {
                console.warn('useQuoteVocabularies: subscription failed', err);
                setVocab({ ...FALLBACK, loaded: true });
            }
        );
    }, [orgId]);

    return vocab;
}
